import { cloneDeep, mergeWith, isEqual } from 'lodash';

export class StudentAdmissionChangeUtils {
    static capitalizeFirstLetter(string) {
        if (!string) return '';
        return string.split(' ').map(word =>
            word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
    }

    static getValidDate(dateValue) {
        if (!dateValue) return null;
        const date = new Date(dateValue);
        return isNaN(date.getTime()) ? null : date;
    }

    static mergeOldFeeInfo(response, selectedStudentData) {
        let mergedOldFeeInfo = { feeInfo: [] };

        // Only proceed if both data sources exist
        if (response?.res?.data?.response?.oldFeeInfo?.feeInfo && selectedStudentData?.oldFeeInfo) {
            // Start with response data as base
            mergedOldFeeInfo = cloneDeep(response.res.data.response.oldFeeInfo);

            // Go through each fee item in the response
            mergedOldFeeInfo.feeInfo = mergedOldFeeInfo.feeInfo.map(responseFeeItem => {
                // Find matching item in selectedStudentData by _id
                const selectedFeeItem = selectedStudentData.oldFeeInfo.find(item =>
                    item._id === responseFeeItem._id
                );

                // If matching item exists, merge with special handling for amountPaid and closingAdvance
                if (selectedFeeItem) {
                    const mergedItem = mergeWith({}, responseFeeItem, selectedFeeItem, (objValue, srcValue, key) => {
                        // Special handling for amountPaid and closingAdvance - use the higher value
                        if (key === 'amountPaid' || key === 'closingAdvance') {
                            const responseAmount = Number(objValue) || 0;
                            const selectedAmount = Number(srcValue) || 0;
                            return Math.max(responseAmount, selectedAmount);
                        }
                        // For other properties, return srcValue (from selectedStudentData) when values differ
                        return !isEqual(objValue, srcValue) ? srcValue : undefined;
                    });
                    
                    // Calculate balance, closingAdvance, and closingDueAmt
                    const totalFee = Number(mergedItem.totalFee) || 0;
                    const amountPaid = Number(mergedItem.amountPaid) || 0;
                    const closingAmount = Number(mergedItem.closingAmount) || 0;
                    
                    // Calculate balance: totalFee - amountPaid
                    mergedItem.balance = totalFee - amountPaid;
                    
                    // Calculate closingAdvance and closingDueAmt based on amountPaid vs closingAmount
                    if (amountPaid > closingAmount) {
                        mergedItem.closingAdvance = amountPaid - closingAmount;
                        mergedItem.closingDueAmt = 0;
                    } else {
                        mergedItem.closingDueAmt = closingAmount - amountPaid;
                        mergedItem.closingAdvance = 0;
                    }
                    
                    return mergedItem;
                }

                // If no matching item found, use the response item as is but calculate balance, closingAdvance, and closingDueAmt
                const responseItem = { ...responseFeeItem };
                const totalFee = Number(responseItem.totalFee) || 0;
                const amountPaid = Number(responseItem.amountPaid) || 0;
                const closingAmount = Number(responseItem.closingAmount) || 0;
                
                // Calculate balance: totalFee - amountPaid
                responseItem.balance = totalFee - amountPaid;
                
                // Calculate closingAdvance and closingDueAmt based on amountPaid vs closingAmount
                if (amountPaid > closingAmount) {
                    responseItem.closingAdvance = amountPaid - closingAmount;
                    responseItem.closingDueAmt = 0;
                } else {
                    responseItem.closingDueAmt = closingAmount - amountPaid;
                    responseItem.closingAdvance = 0;
                }
                
                return responseItem;
            });

            selectedStudentData.oldFeeInfo.forEach(selectedFeeItem => {
                const exists = mergedOldFeeInfo.feeInfo.some(item => item._id === selectedFeeItem._id);
                if (!exists) {
                    // Calculate balance, closingAdvance, and closingDueAmt for items from selectedStudentData
                    const itemToAdd = { ...selectedFeeItem };
                    const totalFee = Number(itemToAdd.totalFee) || 0;
                    const amountPaid = Number(itemToAdd.amountPaid) || 0;
                    const closingAmount = Number(itemToAdd.closingAmount) || 0;
                    
                    // Calculate balance: totalFee - amountPaid
                    itemToAdd.balance = totalFee - amountPaid;
                    
                    // Calculate closingAdvance and closingDueAmt based on amountPaid vs closingAmount
                    if (amountPaid > closingAmount) {
                        itemToAdd.closingAdvance = amountPaid - closingAmount;
                        itemToAdd.closingDueAmt = 0;
                    } else {
                        itemToAdd.closingDueAmt = closingAmount - amountPaid;
                        itemToAdd.closingAdvance = 0;
                    }
                    
                    mergedOldFeeInfo.feeInfo.push(itemToAdd);
                }
            });
        }

        return mergedOldFeeInfo;
    }

    static getAmountColor(enteredAmount, totalAmount) {
        if (enteredAmount === 0) return "#000000"; // Default black
        if (enteredAmount === Number(totalAmount)) return "#2196F3"; // Blue
        return "#FF0000"; // Red
    }

    static sortStepTwoData(data) {
        return [...data].sort((a, b) => {
            if (a.priority !== b.priority) {
                return a.priority - b.priority;
            }
            return a.term - b.term;
        });
    }

    static validateInstallmentPercentages(installmentData, showToast) {
        const feeTypePercentageMap = {};
        let isValid = true;

        for (const installment of installmentData) {
            for (const feeType of installment.feeTypes) {
                if (feeType.percentage > 100) {
                    showToast('error', 'Error', `Percentage for ${feeType.feeType} cannot exceed 100%`);
                    isValid = false;
                }
                if (feeType.percentage < 0) {
                    showToast('error', 'Error', `Percentage for ${feeType.feeType} cannot be negative`);
                    isValid = false;
                }
                if (feeType.percentage > 0) {
                    feeTypePercentageMap[feeType.feeType] = (feeTypePercentageMap[feeType.feeType] || 0) + feeType.percentage;
                }
            }
        }

        if (Object.keys(feeTypePercentageMap).length === 0) {
            showToast('error', 'Error', 'Enter all fee types with non-zero percentages');
            isValid = false;
        }

        for (const [feeType, totalPercentage] of Object.entries(feeTypePercentageMap)) {
            if (totalPercentage !== 100) {
                showToast('error', 'Error', `Total percentage for fee type ${feeType} must be exactly 100%`);
                isValid = false;
            }
        }

        return isValid;
    }

    static calculateTotalEnteredAmount(installmentData, feeType) {
        return installmentData.reduce((totalEntered, installment) => {
            const matchingFee = installment.feeTypes.find(
                fee => fee.feeType === feeType.feeType && fee.feeTypeId === feeType.feeTypeId
            );
            return totalEntered + (matchingFee?.calculatedFee ? Number(matchingFee.calculatedFee) : 0);
        }, 0);
    }

    static getUniqueFeeTypes(selectedFeeStructure) {
        if (!Array.isArray(selectedFeeStructure)) {
            return [];
        }

        const allFeeTypes = selectedFeeStructure.reduce((acc, category) => {
            if (Array.isArray(category?.feeTypes)) {
                acc.push(...category.feeTypes);
            }
            return acc;
        }, []);

        // Create a map to store unique fee types with highest totalFee
        const feeTypeMap = new Map();
        allFeeTypes.forEach(feeType => {
            if (!feeType?._id) return;

            const existingFeeType = feeTypeMap.get(feeType._id);
            if (!existingFeeType || (feeType.totalFee || 0) > (existingFeeType.totalFee || 0)) {
                feeTypeMap.set(feeType._id, feeType);
            }
        });

        return Array.from(feeTypeMap.values());
    }
}
